/**
 * This is for functions that EXPLICITLY only do operations in UTC and return UTC strings
 * (Made a separate file because we keep making the same bug and hopefully this differentiates it)
 */

import { tDateString } from './date';

/**
 * Parse any date string format and return it as a clean UTC date string (YYYY-MM-DD)
 * Strips out all timezone and time information, keeping only the date portion
 *
 * @param dateString - Input date in various formats
 * @returns Clean date string in YYYY-MM-DD format
 *
 * @example
 * parseAsUtc('2025-10-17T15:30:00Z') // returns '2025-10-17'
 * parseAsUtc('2025-10-17T15:30:00+01:00') // returns '2025-10-17'
 * parseAsUtc('2025-10-17 15:30:00') // returns '2025-10-17'
 * parseAsUtc('10/17/2025') // returns '2025-10-17'
 */
export const parseAsUTC = (dateString: string): string => {
  // Remove any whitespace
  const trimmed = dateString.trim();

  // Extract just the date part (YYYY-MM-DD) from various formats
  let datePart: string;

  // Check if it's an ISO string with time (e.g., "2025-10-17T15:30:00Z" or "2025-10-17T15:30:00+01:00")
  if (trimmed.includes('T')) {
    datePart = trimmed.split('T')[0];
  }
  // Check if it has a space separator (e.g., "2025-10-17 15:30:00")
  else if (trimmed.includes(' ')) {
    datePart = trimmed.split(' ')[0];
  }
  // Already just a date
  else {
    datePart = trimmed;
  }

  // Validate the date part matches YYYY-MM-DD format
  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
  if (!dateRegex.test(datePart)) {
    // Try to handle other common formats

    // MM/DD/YYYY or MM-DD-YYYY
    const usFormatRegex = /^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/;
    const usMatch = datePart.match(usFormatRegex);
    if (usMatch) {
      const [, month, day, year] = usMatch;
      datePart = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    // DD/MM/YYYY or DD-MM-YYYY (European format)
    // Note: This is ambiguous with US format for dates like 01/02/2025
    // You may want to remove this or make it configurable
    else {
      const euFormatRegex = /^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/;
      const euMatch = datePart.match(euFormatRegex);
      if (euMatch) {
        const [, day, month, year] = euMatch;
        datePart = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      }
    }
  }

  // Final validation
  if (!dateRegex.test(datePart)) {
    throw new Error(`Invalid date format: ${dateString}. Expected YYYY-MM-DD or similar.`);
  }

  // Return the clean date string
  return datePart;
};

/**
 * Add days to a date string without timezone interference
 * Works consistently in all environments (Jest, browser, etc.)
 */
export const addDays = (date: string, daysToAdd: number): string => {
  // Parse the input to clean YYYY-MM-DD format
  const cleanDate = parseAsUTC(date);

  // Create a UTC date by explicitly setting it to UTC midnight
  const utcString = `${cleanDate}T00:00:00Z`;
  const utcDate = new Date(utcString);

  // Add days using milliseconds to avoid any timezone issues
  const msToAdd = daysToAdd * 24 * 60 * 60 * 1000;
  const newDate = new Date(utcDate.getTime() + msToAdd);

  // Format back to YYYY-MM-DD using UTC methods
  const year = newDate.getUTCFullYear();
  const month = String(newDate.getUTCMonth() + 1).padStart(2, '0');
  const day = String(newDate.getUTCDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

/**
 * Subtract days from a date string without timezone interference
 * Accepts any date format and returns YYYY-MM-DD
 *
 * @param date - Date string in any format (with or without time/timezone)
 * @param daysToSubtract - Number of days to subtract (positive number)
 * @returns Date string in YYYY-MM-DD format
 *
 * @example
 * subDays('2025-10-17', 1) // returns '2025-10-16'
 * subDays('2025-10-17T15:30:00+01:00', 1) // returns '2025-10-16'
 * subDays('10/17/2025', 5) // returns '2025-10-12'
 */
export const subDays = (date: string, daysToSubtract: number): string => {
  // Just call addDays with negative value
  return addDays(date, -daysToSubtract);
};

/**
 * Generate an array of date strings between two dates (inclusive) in UTC
 * All dates are returned in YYYY-MM-DD format without timezone interference
 *
 * @param startDate - Start date in any format (with or without time/timezone)
 * @param endDate - End date in any format (with or without time/timezone)
 * @returns Array of date strings in YYYY-MM-DD format
 *
 * @example
 * generateArrayOfDatesBetween('2025-10-17', '2025-10-20')
 * // returns ['2025-10-17', '2025-10-18', '2025-10-19', '2025-10-20']
 *
 * generateArrayOfDatesBetween('2025-10-17T15:30:00+01:00', '2025-10-19T15:30:00+01:00')
 * // returns ['2025-10-17', '2025-10-18', '2025-10-19']
 */
export const generateArrayOfDatesBetween = (
  startDate: string,
  endDate: string,
  options: {
    start?: 'inclusive' | 'exclusive';
    end?: 'inclusive' | 'exclusive';
  } = {}
): string[] => {
  // Parse both dates to clean YYYY-MM-DD format
  const cleanStartDate = parseAsUTC(startDate);
  const cleanEndDate = parseAsUTC(endDate);

  // Create UTC dates at midnight
  const start = new Date(`${cleanStartDate}T00:00:00Z`);
  const end = new Date(`${cleanEndDate}T00:00:00Z`);

  // Calculate the number of days between the dates
  const daysDifference = Math.round((end.getTime() - start.getTime()) / (24 * 60 * 60 * 1000));

  // Generate array of dates
  const dates: string[] = [];
  for (let i = 0; i <= daysDifference; i++) {
    const currentDate = new Date(start.getTime() + i * 24 * 60 * 60 * 1000);
    const year = currentDate.getUTCFullYear();
    const month = String(currentDate.getUTCMonth() + 1).padStart(2, '0');
    const day = String(currentDate.getUTCDate()).padStart(2, '0');
    dates.push(`${year}-${month}-${day}`);
  }

  // If start date is exclusive, remove the first date
  if (options.start === 'exclusive') {
    dates.shift();
  }

  // If end date is exclusive, remove the last date
  if (options.end === 'exclusive') {
    dates.pop();
  }

  return dates;
};

/**
 * A helper function to count the number of days between two dates
 *
 * Inclusive - so if `startDate` is `2025-10-17` and `endDate` is `2025-10-20`, it will return `3`.
 */
export const countDaysBetween = (startDate: string, endDate: string): number => {
  const days = generateArrayOfDatesBetween(startDate, endDate);
  return days.length;
};

/**
 * A helper function to count the number of NIGHTS between two dates.
 *
 * This is effectively the `countDaysBetween` function, but subtracts 1 from the result.
 *
 * Is deliberately inclusive of `startDate` but EXCLUSIVE of `endDate` - so if `startDate` is `2025-10-17` and `endDate` is `2025-10-20`, it will return `2`.
 */
export const countNightsBetween = (startDate: string, endDate: string): number | '' => {
  if (!startDate || !endDate) {
    return '';
  }
  return countDaysBetween(startDate, endDate) - 1;
};
